/**
 * ExamReviewPanel — Shared Answer Review Component
 *
 * Works for both Mock Tests and Course Exams.
 * Shows per-question correct/wrong/unanswered state,
 * correct answer explanation, and scenario passages.
 */

import { useState, useMemo, useRef, useCallback } from "react";
import { cn } from "@/lib/utils";
import { renderFurigana } from "@/lib/japaneseHelpers";
import { buildMcqDisplayPairs, getMcqOptionTexts, migrateLoadedMcqToIndex } from "@/lib/mcqAnswer";
import { X, ChevronDown, ChevronUp, BookOpen, Volume2, Lightbulb, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";

// ─── Types ───────────────────────────────────────────────────────────────────

interface ReviewScenario {
  id: string;
  instructionsText: string | null;
  imageUrl: string | null;
  audioUrl: string | null;
}

export interface ReviewableQuestion {
  id: string;
  questionText: string;
  options: Record<string, unknown> | null;
  correctAnswer: string | null;
  marks: number;
  imageUrl?: string | null;
  audioUrl?: string | null;
  scenarioId?: string | null;
  scenario?: ReviewScenario | null;
  answerExplanation?: string | null;
  /** Used for section badge (VOCABULARY / GRAMMAR / READING / LISTENING) */
  examCategory?: string;
  type?: string;
}

export interface ExamReviewPanelProps {
  questions: ReviewableQuestion[];
  /** Student's answer map: questionId → selected option index (number) */
  answers: Record<string, unknown>;
  /** Used as shuffle seed for option display order */
  attemptId?: string;
  title?: string;
  onClose?: () => void;
  examType?: "JLPT" | "JFT_BASIC";
  questionLabels?: Record<string, string>;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const CIRCLE_BADGES = ["①", "②", "③", "④", "⑤", "⑥", "⑦", "⑧", "⑨", "⑩"];
function getCircleBadge(index: number) {
  return CIRCLE_BADGES[index] || String(index + 1);
}

const SECTION_JP: Record<string, string> = {
  VOCABULARY: "文字・語彙",
  GRAMMAR: "文法",
  READING: "読解",
  LISTENING: "聴解",
};

const SECTION_COLOR: Record<string, string> = {
  VOCABULARY: "text-blue-700 bg-blue-50 border-blue-200",
  GRAMMAR: "text-amber-700 bg-amber-50 border-amber-200",
  READING: "text-emerald-700 bg-emerald-50 border-emerald-200",
  LISTENING: "text-purple-700 bg-purple-50 border-purple-200",
};

type FilterType = "all" | "correct" | "incorrect" | "unanswered";

function getAnswerState(
  q: ReviewableQuestion,
  answers: Record<string, unknown>,
): "correct" | "incorrect" | "unanswered" {
  const userAnswer = answers[q.id];
  if (userAnswer === undefined || userAnswer === null || userAnswer === "") {
    return "unanswered";
  }

  const userAnsStr = String(userAnswer ?? "").trim();
  const correctAnsStr = String(q.correctAnswer ?? "").trim();

  if (userAnsStr === correctAnsStr) {
    return "correct";
  }

  // Fallback for option ID matching
  let matchedByOption = false;
  if (q.options && typeof q.options === "object") {
    const optionsData = q.options as any;
    const optsArray = optionsData.options || optionsData;
    if (Array.isArray(optsArray)) {
      const correctOption = optsArray.find((o: any) => o.id === correctAnsStr);
      if (correctOption) {
        const matchVal = String(correctOption.text || correctOption.imageUrl || "").trim();
        if (matchVal === userAnsStr) {
          matchedByOption = true;
        }
      }
    }
  }

  if (matchedByOption) return "correct";

  return "incorrect";
}

// ─── Sub-component: ScenarioBlock ─────────────────────────────────────────────

function ScenarioBlock({ scenario }: { scenario: ReviewScenario }) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="mb-5 rounded-xl border border-stone-200 overflow-hidden bg-[#faf9f5] select-none">
      <button
        type="button"
        className="w-full flex items-center justify-between px-5 py-3 text-xs font-bold text-stone-600 uppercase tracking-wider bg-stone-100/80 border-b border-stone-200 hover:bg-stone-200/50 transition-colors"
        onClick={() => setExpanded((prev) => !prev)}
      >
        <span className="flex items-center gap-1.5">
          <BookOpen size={12} className="text-stone-500" />
          【読解・聴解 本文 / Passage Reference】
        </span>
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {expanded && (
        <div className="p-5 space-y-3">
          {scenario.instructionsText && (
            <div
              className="text-[0.95rem] leading-[2] text-stone-800 whitespace-pre-wrap"
              
            >
              {renderFurigana(scenario.instructionsText)}
            </div>
          )}
          {scenario.imageUrl && (
            <img
              src={scenario.imageUrl}
              alt=""
              className="max-h-[550px] w-full rounded-lg border border-stone-200 bg-white object-contain my-4 max-w-3xl mx-auto pointer-events-none"
            />
          )}
          {scenario.audioUrl && (
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-[10px] font-semibold text-stone-500 uppercase tracking-wider">
                <Volume2 size={11} /> 聴解音声 (Listening Audio — Review)
              </div>
              <audio controls className="w-full max-w-md h-9" src={scenario.audioUrl} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Sub-component: ReviewQuestionCard ────────────────────────────────────────

function ReviewQuestionCard({
  q,
  index,
  answers,
  attemptId,
  state,
  examType,
  questionLabel,
}: {
  q: ReviewableQuestion;
  index: number;
  answers: Record<string, unknown>;
  attemptId?: string;
  state: "correct" | "incorrect" | "unanswered";
  examType?: "JLPT" | "JFT_BASIC";
  questionLabel?: string;
}) {
  const chosenRaw = answers[q.id];
  let chosenIndex: number | undefined;
  const migrated = migrateLoadedMcqToIndex(q as any, chosenRaw);
  if (typeof migrated === "number") {
    chosenIndex = migrated;
  } else if (typeof chosenRaw === "string") {
    const parsed = parseInt(chosenRaw.trim(), 10);
    if (!isNaN(parsed) && String(parsed) === chosenRaw.trim()) {
      chosenIndex = parsed;
    }
  }
  const optPairs = buildMcqDisplayPairs(
    q,
    true,
    attemptId ? `${attemptId}:${q.id}:mcqopts` : `review:${q.id}:mcqopts`,
  );
  const optionType =
    (q.options as { metadata?: any } | null | undefined)?.metadata?.optionType || "TEXT";

  const sectionKey = q.examCategory || "";
  const isJFT = examType === "JFT_BASIC";
  const sectionJp = isJFT && sectionKey === "GRAMMAR" ? "会話・表現" : (SECTION_JP[sectionKey] || sectionKey);
  const sectionColorClass = SECTION_COLOR[sectionKey] || "text-stone-600 bg-stone-50 border-stone-200";

  // Find what the correct option display index is (for showing "Correct: ①")
  const optsPairsForCorrect = buildMcqDisplayPairs(q, false, "");
  const targetCorrect = typeof q.correctAnswer === "string" 
    ? q.correctAnswer.trim() 
    : String(q.correctAnswer ?? "").trim();
  let correctOrigIndex = optsPairsForCorrect.findIndex(
    (p) => p.id === targetCorrect || (p.text?.trim() || p.imageUrl?.trim() || "") === targetCorrect
  );

  if (correctOrigIndex === -1) {
    const parsedTarget = parseInt(targetCorrect, 10);
    if (!isNaN(parsedTarget) && parsedTarget >= 0 && parsedTarget < optsPairsForCorrect.length && String(parsedTarget) === targetCorrect) {
      correctOrigIndex = parsedTarget;
    }
  }
  const correctDisplayPair = optPairs.find((p) => p.origIndex === correctOrigIndex);
  const correctDisplayIndex = correctDisplayPair ? optPairs.indexOf(correctDisplayPair) : -1;

  const chosenDisplayPair = optPairs.find((p) => p.origIndex === chosenIndex);
  const chosenDisplayIndex = chosenDisplayPair ? optPairs.indexOf(chosenDisplayPair) : -1;

  return (
    <div
      id={`review-q-${q.id}`}
      className={cn(
        "bg-white border p-6 sm:p-8 shadow-xs  relative scroll-mt-6 rounded-2xl transition-all",
        state === "correct"
          ? "border-emerald-200 ring-1 ring-emerald-100"
          : state === "incorrect"
            ? "border-rose-200 ring-1 ring-rose-100"
            : "border-stone-200",
      )}
      
    >
      {/* Subtle inner border decoration */}
      <div className="absolute inset-1.5 border border-stone-100 pointer-events-none opacity-40 rounded-xl select-none" />

      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-stone-200 pb-3 mb-4 font-sans select-none">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-stone-800 text-sm">{questionLabel ? `問 ${questionLabel}` : `問 ${index + 1}`}</span>
          {sectionKey && (
            <span
              className={cn(
                "text-[9px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider border",
                sectionColorClass,
              )}
            >
              {sectionJp}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[10px] text-stone-500 font-medium">配点 {q.marks}点</span>
          {state === "unanswered" ? (
            <span className="bg-stone-100 text-stone-600 border border-stone-200 px-2.5 py-0.5 rounded-full text-[10px] font-bold">
              ○ 未解答 (Unanswered)
            </span>
          ) : state === "correct" ? (
            <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-0.5 rounded-full text-[10px] font-bold">
              ✓ 正解 (Correct)
            </span>
          ) : (
            <span className="bg-rose-50 text-rose-700 border border-rose-200 px-2.5 py-0.5 rounded-full text-[10px] font-bold">
              ✗ 不正解 (Incorrect)
            </span>
          )}
        </div>
      </div>

      {/* Scenario passage (shown per-question if present) */}
      {q.scenario && <ScenarioBlock scenario={q.scenario} />}

      {/* Question text */}
      <div className="text-[1.05rem] leading-relaxed text-stone-900 mb-4 pb-4 whitespace-pre-wrap" >
        {renderFurigana(q.questionText)}
      </div>

      {/* Question-level media */}
      {q.imageUrl && (
        <img
          src={q.imageUrl}
          alt=""
          className="max-h-[480px] w-full rounded-xl border border-stone-200 object-contain my-3 max-w-2xl mx-auto pointer-events-none"
        />
      )}
      {q.audioUrl && (
        <div className="my-3 max-w-sm">
          <div className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-1 font-sans">
            聴解音声 (Review — Unlimited Replay)
          </div>
          <audio controls className="w-full h-9" src={q.audioUrl} />
        </div>
      )}

      {/* Answer Options */}
      {optionType === "IMAGE" ? (
        <div className="grid grid-cols-2 gap-3 mt-4 select-none">
          {optPairs.map(({ origIndex, imageUrl }, oIdx) => {
            const isOptionCorrect = origIndex === correctOrigIndex;
            const isOptionChosen = chosenIndex === origIndex;
            return (
              <div
                key={origIndex}
                className={cn(
                  "relative flex flex-col items-center justify-center rounded-xl border p-3 aspect-square transition-all shadow-xs",
                  isOptionCorrect
                    ? "border-emerald-500 bg-emerald-50/30 ring-2 ring-emerald-400/30"
                    : isOptionChosen
                      ? "border-rose-400 bg-rose-50/30 ring-2 ring-rose-300/30"
                      : "border-stone-200 bg-white",
                )}
              >
                <span
                  className={cn(
                    "absolute top-2.5 left-2.5 text-sm font-bold select-none leading-none",
                    isOptionCorrect
                      ? "text-emerald-600"
                      : isOptionChosen
                        ? "text-rose-500"
                        : "text-stone-400",
                  )}
                >
                  {getCircleBadge(oIdx)}
                </span>
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt=""
                    className="max-h-[78%] max-w-[78%] object-contain rounded pointer-events-none"
                  />
                ) : (
                  <span className="text-[10px] text-stone-400 italic">(No Image)</span>
                )}
                <div className="absolute bottom-2 left-2 right-2 flex justify-center gap-1 flex-wrap">
                  {isOptionCorrect && (
                    <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 rounded-full">
                      正解
                    </span>
                  )}
                  {!isOptionCorrect && isOptionChosen && (
                    <span className="text-[9px] font-bold text-rose-700 bg-rose-100 border border-rose-300 px-1.5 py-0.5 rounded-full">
                      選択
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 mt-4 select-none">
          {optPairs.map(({ text, origIndex, imageUrl }, oIdx) => {
            const isOptionCorrect = origIndex === correctOrigIndex;
            const isOptionChosen = chosenIndex === origIndex;
            return (
              <div
                key={origIndex}
                className={cn(
                  "w-full rounded-xl border p-3.5 flex gap-3 text-[15px] transition-all",
                  isOptionCorrect
                    ? "border-emerald-400 bg-emerald-50/40 text-stone-900"
                    : isOptionChosen
                      ? "border-rose-400 bg-rose-50/40 text-stone-900"
                      : "border-stone-200 bg-white text-stone-700",
                )}
              >
                <span
                  className={cn(
                    "text-lg shrink-0 font-bold leading-none pt-0.5",
                    isOptionCorrect
                      ? "text-emerald-600"
                      : isOptionChosen
                        ? "text-rose-500"
                        : "text-stone-400",
                  )}
                >
                  {getCircleBadge(oIdx)}
                </span>
                <div className="flex-1 min-w-0 leading-snug">
                  {text && <span>{renderFurigana(text)}</span>}
                  {imageUrl && optionType === "MIXED" && (
                    <img
                      src={imageUrl}
                      alt=""
                      className="mt-1.5 max-h-14 object-contain rounded border border-stone-200 bg-white"
                    />
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {isOptionCorrect && (
                    <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100/90 px-2 py-0.5 rounded-full whitespace-nowrap">
                      正解 ✓
                    </span>
                  )}
                  {!isOptionCorrect && isOptionChosen && (
                    <span className="text-[10px] font-bold text-rose-700 bg-rose-100/90 px-2 py-0.5 rounded-full whitespace-nowrap">
                      選択 ✗
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Answer Summary Row */}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 font-sans text-xs select-none">
        <div
          className={cn(
            "rounded-xl border px-4 py-3 flex flex-col gap-0.5",
            state === "unanswered"
              ? "bg-stone-50 border-stone-200"
              : state === "correct"
                ? "bg-emerald-50/50 border-emerald-200"
                : "bg-rose-50/50 border-rose-200",
          )}
        >
          <span className="text-[9px] font-bold uppercase tracking-widest text-stone-400 mb-0.5">
            あなたの解答 / Your Answer
          </span>
          {state === "unanswered" ? (
            <span className="text-stone-500 italic text-sm">未解答 (Not answered)</span>
          ) : (
            <span
              className={cn(
                "text-sm font-bold ",
                state === "correct" ? "text-emerald-700" : "text-rose-700",
              )}
            >
              {chosenDisplayIndex >= 0 ? getCircleBadge(chosenDisplayIndex) : "?"}{" "}
              {chosenDisplayPair?.text && (
                <span className="font-normal">{renderFurigana(chosenDisplayPair.text)}</span>
              )}
            </span>
          )}
        </div>

        <div className="rounded-xl border bg-emerald-50/50 border-emerald-200 px-4 py-3 flex flex-col gap-0.5">
          <span className="text-[9px] font-bold uppercase tracking-widest text-stone-400 mb-0.5">
            正解 / Correct Answer
          </span>
          <span className="text-sm font-bold text-emerald-700">
            {correctDisplayIndex >= 0 ? getCircleBadge(correctDisplayIndex) : "?"}{" "}
            {correctDisplayPair?.text && (
              <span className="font-normal">{renderFurigana(correctDisplayPair.text)}</span>
            )}
            {optionType === "IMAGE" && correctDisplayPair?.imageUrl && (
              <img
                src={correctDisplayPair.imageUrl}
                alt=""
                className="mt-1.5 h-10 object-contain rounded border border-emerald-200 bg-white"
              />
            )}
          </span>
        </div>
      </div>

      {/* Explanation Box — only shown if admin set an explanation */}
      {(() => {
        const metadata = (q.options as { metadata?: any } | null | undefined)?.metadata || {};
        const explanation = q.answerExplanation || metadata.answerExplanation;
        return explanation && explanation.trim() ? (
          <div className="mt-5 border border-amber-200 bg-amber-50/60 rounded-xl p-4 font-sans">
            <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-widest text-amber-700 mb-2">
              <Lightbulb size={11} className="text-amber-600" />
              解説 / Answer Explanation
            </div>
            <div
              className="text-sm text-stone-800 leading-relaxed whitespace-pre-wrap"
              
            >
              {renderFurigana(explanation)}
            </div>
          </div>
        ) : null;
      })()}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function ExamReviewPanel({
  questions,
  answers,
  attemptId,
  title = "解答解説 / Answer Review",
  onClose,
  examType,
  questionLabels,
}: ExamReviewPanelProps) {
  const [filter, setFilter] = useState<FilterType>("all");
  const containerRef = useRef<HTMLDivElement>(null);

  // Compute state for each question
  const questionStates = useMemo(
    () =>
      questions.map((q) => ({
        q,
        state: getAnswerState(q, answers),
      })),
    [questions, answers],
  );

  const counts = useMemo(() => {
    let correct = 0;
    let incorrect = 0;
    let unanswered = 0;
    for (const { state } of questionStates) {
      if (state === "correct") correct++;
      else if (state === "incorrect") incorrect++;
      else unanswered++;
    }
    return { correct, incorrect, unanswered, total: questions.length };
  }, [questionStates, questions.length]);

  const filteredQuestions = useMemo(() => {
    if (filter === "all") return questionStates;
    return questionStates.filter(({ state }) => state === filter);
  }, [questionStates, filter]);

  const scrollToQuestion = useCallback((qId: string) => {
    const el = document.getElementById(`review-q-${qId}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  // Group consecutive scenario questions for rendering
  type RenderGroup =
    | { type: "scenario"; scenario: ReviewScenario; items: typeof filteredQuestions }
    | { type: "standalone"; item: (typeof filteredQuestions)[0] };

  const renderGroups = useMemo<RenderGroup[]>(() => {
    const groups: RenderGroup[] = [];
    let currentScenarioGroup: { scenario: ReviewScenario; items: typeof filteredQuestions } | null = null;

    for (const item of filteredQuestions) {
      if (item.q.scenario && item.q.scenarioId) {
        if (
          currentScenarioGroup &&
          currentScenarioGroup.scenario.id === item.q.scenarioId
        ) {
          currentScenarioGroup.items.push(item);
        } else {
          currentScenarioGroup = { scenario: item.q.scenario, items: [item] };
          groups.push({ type: "scenario", ...currentScenarioGroup });
        }
      } else {
        currentScenarioGroup = null;
        groups.push({ type: "standalone", item });
      }
    }
    return groups;
  }, [filteredQuestions]);

  // Map from question id → global index (1-based display number)
  const globalIndexMap = useMemo(() => {
    const m: Record<string, number> = {};
    questions.forEach((q, i) => { m[q.id] = i; });
    return m;
  }, [questions]);

  const FILTER_BUTTONS: { key: FilterType; label: string; count: number; color: string }[] = [
    { key: "all", label: "全て / All", count: counts.total, color: "text-stone-700 bg-stone-100 border-stone-200 data-[active=true]:bg-stone-900 data-[active=true]:text-white data-[active=true]:border-stone-900" },
    { key: "correct", label: "正解 / Correct", count: counts.correct, color: "text-emerald-700 bg-emerald-50 border-emerald-200 data-[active=true]:bg-emerald-600 data-[active=true]:text-white data-[active=true]:border-emerald-600" },
    { key: "incorrect", label: "不正解 / Wrong", count: counts.incorrect, color: "text-rose-700 bg-rose-50 border-rose-200 data-[active=true]:bg-rose-600 data-[active=true]:text-white data-[active=true]:border-rose-600" },
    { key: "unanswered", label: "未解答 / Skipped", count: counts.unanswered, color: "text-stone-600 bg-stone-50 border-stone-200 data-[active=true]:bg-stone-600 data-[active=true]:text-white data-[active=true]:border-stone-600" },
  ];

  return (
    <div className="bg-[#f4f3f0] font-sans" ref={containerRef}>
      {/* ── Review Header ──────────────────────────────────────────────────── */}
      <div className="sticky top-0 z-20 bg-white border-b border-stone-200 shadow-xs">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2
                className="text-xl font-bold tracking-wide text-stone-900"
                
              >
                {title}
              </h2>
              <p className="text-[10px] uppercase tracking-widest text-stone-400 font-semibold mt-0.5 font-sans">
                Question Review &amp; Answer Comparison
              </p>
            </div>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="p-2 rounded-xl text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors shrink-0"
                title="Close Review"
              >
                <X size={18} />
              </button>
            )}
          </div>

          {/* Stats Summary Row */}
          <div className="grid grid-cols-4 gap-2 mb-4">
            {[
              { label: "Total", value: counts.total, color: "text-stone-700" },
              { label: "Correct ✓", value: counts.correct, color: "text-emerald-600" },
              { label: "Wrong ✗", value: counts.incorrect, color: "text-rose-600" },
              { label: "Skipped", value: counts.unanswered, color: "text-stone-500" },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-stone-50 border border-stone-200 rounded-xl px-3 py-2 text-center">
                <div className={cn("text-lg font-bold font-mono leading-none", color)}>{value}</div>
                <div className="text-[9px] text-stone-400 font-semibold uppercase tracking-wider mt-0.5">{label}</div>
              </div>
            ))}
          </div>

          {/* Filter Pills */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <Filter size={11} className="text-stone-400 shrink-0" />
            {FILTER_BUTTONS.map(({ key, label, count, color }) => (
              <button
                key={key}
                type="button"
                data-active={filter === key}
                onClick={() => setFilter(key)}
                className={cn(
                  "inline-flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-bold border transition-all",
                  color,
                )}
              >
                {label}
                <span className="opacity-70 font-mono">({count})</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Question Navigator Grid (compact) ─────────────────────────────── */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
        <div className="bg-white border border-stone-200 rounded-2xl p-4 mb-6">
          <div className="text-[9px] font-bold uppercase tracking-widest text-stone-400 mb-3 font-sans">
            問題ナビゲーター / Question Navigator
          </div>
          <div className="flex flex-wrap gap-1.5">
            {questionStates.map(({ q, state }, idx) => {
              const isVisible = filteredQuestions.some((fq) => fq.q.id === q.id);
              return (
                <button
                  key={q.id}
                  type="button"
                  title={`Question ${idx + 1} — ${state}`}
                  onClick={() => {
                    if (filter !== "all") setFilter("all");
                    setTimeout(() => scrollToQuestion(q.id), 50);
                  }}
                  className={cn(
                    "w-8 h-8 rounded-lg text-[11px] font-bold font-mono border transition-all flex items-center justify-center shrink-0",
                    state === "correct"
                      ? "bg-emerald-100 border-emerald-300 text-emerald-700 hover:bg-emerald-200"
                      : state === "incorrect"
                        ? "bg-rose-100 border-rose-300 text-rose-700 hover:bg-rose-200"
                        : "bg-stone-100 border-stone-200 text-stone-500 hover:bg-stone-200",
                    !isVisible && filter !== "all" && "opacity-30",
                  )}
                >
                  {questionLabels ? questionLabels[q.id] : idx + 1}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-stone-100 text-[9px] font-semibold uppercase tracking-wider text-stone-400 select-none font-sans">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-emerald-100 border border-emerald-300 inline-block" />正解</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-rose-100 border border-rose-300 inline-block" />不正解</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-stone-100 border border-stone-300 inline-block" />未解答</span>
          </div>
        </div>

        {/* ── Question Cards ─────────────────────────────────────────────── */}
        {filteredQuestions.length === 0 ? (
          <div className="text-center py-16 text-stone-400 font-sans text-sm">
            {filter === "correct" && "No correct answers in this session."}
            {filter === "incorrect" && "No incorrect answers — great job! 🎉"}
            {filter === "unanswered" && "All questions were answered."}
          </div>
        ) : (
          <div className="space-y-6 pb-16">
            {renderGroups.map((group, gi) => {
              if (group.type === "standalone") {
                const { q, state } = group.item;
                return (
                  <ReviewQuestionCard
                    key={q.id}
                    q={q}
                    index={globalIndexMap[q.id] ?? gi}
                    answers={answers}
                    attemptId={attemptId}
                    state={state}
                    examType={examType}
                    questionLabel={questionLabels ? questionLabels[q.id] : undefined}
                  />
                );
              }

              // Scenario group — render passage once, then all sub-questions
              return (
                <div key={`sg-${gi}`} className="space-y-4">
                  <div className="bg-[#faf9f5] border border-stone-200 rounded-2xl p-5 sm:p-6 shadow-xs">
                    <div className="text-[9px] font-bold uppercase tracking-widest text-stone-400 mb-3 font-sans">
                      シナリオ / Scenario Block
                    </div>
                    {group.scenario.instructionsText && (
                      <div
                        className="text-[0.97rem] leading-[2.1] text-stone-900 whitespace-pre-wrap mb-3"
                        
                      >
                        {renderFurigana(group.scenario.instructionsText)}
                      </div>
                    )}
                    {group.scenario.imageUrl && (
                      <img
                        src={group.scenario.imageUrl}
                        alt=""
                        className="max-h-[550px] w-full rounded-xl border border-stone-200 bg-white mt-2 object-contain max-w-3xl mx-auto pointer-events-none"
                      />
                    )}
                    {group.scenario.audioUrl && (
                      <div className="mt-3">
                        <div className="text-[9px] font-bold text-stone-400 uppercase tracking-wider mb-1">
                          聴解音声 / Listening Audio (Review — Unlimited Replay)
                        </div>
                        <audio controls className="w-full max-w-md h-9" src={group.scenario.audioUrl} />
                      </div>
                    )}
                  </div>

                  {group.items.map(({ q, state }) => (
                    <ReviewQuestionCard
                      key={q.id}
                      q={{ ...q, scenario: null }} // Don't re-render scenario inside card
                      index={globalIndexMap[q.id] ?? 0}
                      answers={answers}
                      attemptId={attemptId}
                      state={state}
                      examType={examType}
                      questionLabel={questionLabels ? questionLabels[q.id] : undefined}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
